"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic'; // Import dynamic

// Dynamically import the ProductComparison section from the same directory
const ProductComparisonSection = dynamic(() => import('./ProductComparison'), {
  loading: () => <div className="py-16 px-4 min-h-[50vh] flex items-center justify-center">Loading Product Comparison...</div>,
  ssr: false // Typically safe for sections without server-side data needs
});

const InvestmentOverview = dynamic(() => import('./InvestmentOverview'), { ssr: false });
const RevenueOpportunities = dynamic(() => import('./RevenueOpportunities'), { ssr: false });
const RoiFactors = dynamic(() => import('./RoiFactors'), { ssr: false });
const EfficiencyCaseStudy = dynamic(() => import('./EfficiencyCaseStudy'), { ssr: false });
const CallToActionSection = dynamic(() => import('./CallToActionSection'), { ssr: false });

export default function ROI() {
  const [animationsReady, setAnimationsReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationsReady(true);
    }, 200); // Reduced delay back to 200ms

    return () => clearTimeout(timer); // Cleanup timer on unmount
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-12 px-4 bg-gradient-to-br from-[#1a2942] via-[#234563] to-[#1e5e52] text-white overflow-hidden flex flex-col">
        {/* Enhanced background with shapes to mask image artifacts */}
        
        <div className="absolute inset-0 opacity-20">
          <div className={`absolute top-20 right-1/4 w-96 h-96 rounded-full bg-[#38b5a0] blur-2xl ${animationsReady ? 'animate-pulse' : ''}`}></div>
          <div className={`absolute bottom-10 left-1/5 w-80 h-80 rounded-full bg-[#38b5a0] blur-2xl ${animationsReady ? 'animate-pulse' : ''}`} style={{animationDelay: '1s'}}></div>
          <div className={`absolute top-40 left-1/4 w-64 h-64 rounded-full bg-[#2389db] blur-2xl ${animationsReady ? 'animate-pulse' : ''}`} style={{animationDelay: '1.5s'}}></div>
          <div className={`absolute bottom-40 right-10 w-72 h-72 rounded-full bg-[#2389db] blur-2xl ${animationsReady ? 'animate-pulse' : ''}`} style={{animationDelay: '2s'}}></div>
        </div>
        

        <div className="max-w-6xl mx-auto relative z-10 flex-grow flex items-center">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div> 
              <span 
                className={`inline-block bg-[#38b5a0]/20 text-white text-lg mb-4 py-1 px-4 rounded-full border border-[#38b5a0]/30 opacity-0 ${animationsReady ? 'animate-[fadeSlideUp_0.7s_forwards]' : ''}`}
                style={{ animationDelay: animationsReady ? '0.2s' : undefined }}
              >
                Strategic Investment
              </span>
              <h1 
                className={`text-3xl md:text-5xl font-bold mb-6 leading-tight`}
              >
                I-motion EMS: A Premium Investment with Significant Returns
              </h1>
              <p 
                className={`text-xl max-w-3xl opacity-0 ${animationsReady ? 'animate-[fadeSlideUp_0.7s_forwards]' : ''}`}
                style={{ animationDelay: animationsReady ? '0.4s' : undefined }}
              >
                Understand the business case and ROI potential of adding I-motion
                EMS technology to your facility.
              </p>
              <div 
                className={`mt-8 opacity-0 ${animationsReady ? 'animate-[fadeSlideUp_0.7s_forwards]' : ''}`}
                style={{ animationDelay: animationsReady ? '0.5s' : undefined }}
              >
                <Link 
                  href="/contact" 
                  className="inline-flex items-center bg-gradient-to-r from-[#2389db] to-[#165588] text-white font-medium px-6 sm:px-8 py-3 rounded-lg hover:from-[#165588] hover:to-[#2389db] transition-all shadow-lg shadow-[#2389db]/30 text-center group"
                >
                  <span className="inline-flex items-center">
                    Request ROI Analysis
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform">
                      <path fillRule="evenodd" d="M2 10a.75.75 0 01.75-.75h12.59l-2.1-1.95a.75.75 0 111.02-1.1l3.5 3.25a.75.75 0 010 1.1l-3.5 3.25a.75.75 0 11-1.02-1.1l2.1-1.95H2.75A.75.75 0 012 10z" clipRule="evenodd" />
                    </svg>
                  </span>
                </Link>
              </div>
            </div>
            
            {/* Hero Image - Restored, priority removed */}
            
            <div className="hidden md:flex justify-center items-center relative">
              {/* Glow effect remains commented out for now as it wasn't the FCP cause */}
              {/* <div className="absolute w-full h-full max-w-[280px] max-h-[420px] bg-[#38b5a0]/10 blur-xl rounded-full animate-pulse"></div> */}
              
              <div className="relative z-10">
                <Image
                  src="/images/woman-imotion-suit.png"
                  alt="Woman in I-motion EMS suit"
                  width={267}
                  height={400}
                  className={`object-contain animate-float will-change-transform`}
                  priority
                />
              </div>
            </div>
            
          </div>
        </div>

        {/* Wave divider */}
        
        <div className="absolute bottom-0 left-0 w-full overflow-hidden">
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="w-full h-[60px]"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-white"
            ></path>
          </svg>
        </div>
        
      </section>

      {/* Add the following style tag in your component for animations */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeSlideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Dynamically imported Investment Overview Section */}
      <InvestmentOverview />

      {/* Dynamically imported Revenue Opportunities Section */}
      <RevenueOpportunities />

      {/* Dynamically imported ROI Factors Section */}
      <RoiFactors />

      {/* Dynamically imported Efficiency & Case Study Section */}
      <EfficiencyCaseStudy />

      {/* Dynamically imported Product Comparison Section */}
      <ProductComparisonSection />

      {/* Dynamically imported Call to Action Section */}
      <CallToActionSection />
    </div>
  );
}
